import React, { useState } from 'react';
import dataService from '../services/dataService';

const StateList = ({ selectedMetric, stateData, onStateClick, selectedState }) => {
    const [sortOrder, setSortOrder] = useState('name'); // 'name' or 'value'
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

    if (!selectedMetric || !stateData) {
        return (
            <div style={{ padding: '20px' }}>
                <h3>All States (Alphabetical)</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Select a metric above to see all state values
                </p>
            </div>
        );
    }

    const description = dataService.getDataDescription(selectedMetric);
    
    const formatValue = (value) => {
        if (value === null || value === undefined) return 'N/A';
        
        const source = dataService.dataSources[selectedMetric];
        if (!source || !source.unit) return value;

        switch (source.unit) {
            case 'dollars':
                return `$${value.toLocaleString()}`;
            case 'dollars per hour':
                return `$${value.toFixed(2)}`;
            case 'dollars per month':
                return `$${value.toLocaleString()}`;
            case 'percentage':
                return `${value}%`;
            case 'people':
                return value.toLocaleString();
            case 'years':
                return `${value} yrs`;
            case 'births per 1000 people':
                return `${value}‰`;
            case 'deaths per 1000 births':
                return `${value}‰`;
            case 'incidents per 100,000 people':
                return `${value}/100k`;
            case 'per 100,000 people':
                return `${value}/100k`;
            case 'people per square mile':
                return `${value}/mi²`;
            case 'students per teacher':
                return `${value}:1`;
            case 'degrees Fahrenheit':
                return `${value}°F`;
            case 'Mbps':
                return `${value}Mb`;
            case 'metric tons per person':
                return `${value}t`;
            case 'violations per 1000 people':
                return `${value}/1k`;
            case 'percentage of total energy':
                return `${value}%`;
            case 'percentage of bridges':
                return `${value}%`;
            case 'percentage of eligible voters':
                return `${value}%`;
            case 'AQI (lower is better)':
                return `${value}`;
            case 'index (0-100)':
                return `${value}`;
            case 'index (higher is more affordable)':
                return `${value}`;
            case 'score (0-100, higher is better)':
                return `${value}`;
            case 'rank (1-50, lower is better)':
                return `#${value}`;
            case 'rank (1-50, lower is better/less corrupt)':
                return `#${value}`;
            case 'grade (A+ to F, A+ is strongest)':
                return value;
            default:
                return value;
        }
    };

    // Create sorted array of states
    const stateEntries = Object.entries(stateData)
        .filter(([stateCode, value]) => value !== null && value !== undefined)
        .map(([stateCode, value]) => ({
            stateCode,
            stateName: dataService.getStateName(stateCode),
            value,
            formattedValue: formatValue(value)
        }));

    // Sort the array
    const sortedStates = [...stateEntries].sort((a, b) => {
        let comparison = 0;
        
        if (sortOrder === 'name') {
            comparison = a.stateName.localeCompare(b.stateName);
        } else if (sortOrder === 'value') {
            if (typeof a.value === 'number' && typeof b.value === 'number') {
                comparison = a.value - b.value;
            } else {
                comparison = String(a.value).localeCompare(String(b.value));
            }
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
    });

    const handleSort = (newSortOrder) => {
        if (sortOrder === newSortOrder) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortOrder(newSortOrder);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column) => {
        if (sortOrder !== column) return '↕️';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '15px'
            }}>
                <h3>All States - {description}</h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                    {stateEntries.length} states with data
                </div>
            </div>
            
            {/* Sort Controls */}
            <div style={{ 
                marginBottom: '15px', 
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
            }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                    Sort by:
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => handleSort('name')}
                        style={{
                            padding: '5px 10px',
                            backgroundColor: sortOrder === 'name' ? '#4CAF50' : '#fff',
                            color: sortOrder === 'name' ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        State Name {getSortIcon('name')}
                    </button>
                    <button
                        onClick={() => handleSort('value')}
                        style={{
                            padding: '5px 10px',
                            backgroundColor: sortOrder === 'value' ? '#4CAF50' : '#fff',
                            color: sortOrder === 'value' ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        Value {getSortIcon('value')}
                    </button>
                </div>
            </div>

            {/* State List */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '8px',
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '10px'
            }}>
                {sortedStates.map(({ stateCode, stateName, value, formattedValue }) => (
                    <div
                        key={stateCode}
                        onClick={() => onStateClick(stateCode)}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: selectedState === stateCode ? '#e3f2fd' : '#fff',
                            border: selectedState === stateCode ? '2px solid #4CAF50' : '1px solid #e9ecef',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '13px',
                            transition: 'all 0.2s ease',
                            breakInside: 'avoid'
                        }}
                        onMouseOver={(e) => {
                            if (selectedState !== stateCode) {
                                e.target.style.backgroundColor = '#f8f9fa';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (selectedState !== stateCode) {
                                e.target.style.backgroundColor = '#fff';
                            }
                        }}
                    >
                        <span style={{ 
                            fontWeight: '500',
                            color: selectedState === stateCode ? '#4CAF50' : '#333'
                        }}>
                            {stateName}:
                        </span>
                        <span style={{
                            fontWeight: 'bold',
                            color: selectedState === stateCode ? '#4CAF50' : '#666',
                            marginLeft: '8px'
                        }}>
                            {formattedValue}
                        </span>
                    </div>
                ))}
            </div>
            
            {stateEntries.length === 0 && (
                <div style={{
                    padding: '20px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    color: '#856404',
                    textAlign: 'center'
                }}>
                    No data available for {description}
                </div>
            )}
        </div>
    );
};

export default StateList;